import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  Fade,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  api,
  type PodcastVideoRecord,
  type TopViewingSitePlan,
  type VideoMetadata,
} from '../../services/api';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  PlayArrowRounded as PlayArrowRoundedIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  MusicNote as TikTokIcon,
  MenuBook as RedNoteIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Add as AddIcon,
  Share as ShareIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  CheckCircleRounded as CheckCircleRoundedIcon,
} from '@mui/icons-material';

// ---------------------------------------------------------------------------
// PodcastVideosTab
//
// Project-related short-form podcast content sourced from various social
// platforms (YouTube, TikTok, Instagram, etc.). Layout mirrors the
// Project Essentials Videos page but with two key differences:
//   1. Cards expose the creator (avatar + name) above the views line.
//   2. Thumbnails are landscape (16:9) for YouTube and portrait (9:16)
//      for TikTok/Instagram, matching the native aspect ratio of each
//      source so the cards feel authentic to their platform of origin.
// ---------------------------------------------------------------------------

export interface PodcastVideosTabProps {
  analyticsCea?: string | null;
  // True for the admin role. Admins can always add videos; everyone else
  // needs ACCELERATOR, VIDEO-UPLOADER, or project-lead (Head / Deputy /
  // lead-less IC) before the Add Video button is surfaced.
  isAdmin?: boolean;
  isAccelerator?: boolean;
  isVideoUploader?: boolean;
}

export type PodcastSource =
  | 'youtube'
  | 'tiktok'
  | 'instagram'
  | 'facebook'
  | 'rednote';

// Hard cap on how many projects a single podcast video can be tagged
// to. Surfaced in the Add dialog helper text so users see the rule
// before they hit the limit. Keep small enough that the chip strip
// stays readable on mobile.
export const MAX_PROJECT_TAGS = 3;

export interface PodcastVideoItem {
  id: string;
  title: string;
  source: PodcastSource;
  thumbnail: string | null;
  streamUrl: string;
  views: number;
  durationSeconds: number;
  createdAt: string;
  creatorName: string;
  creatorAvatar: string | null;
  // Primary tagged project. For mock data this is the only tag; for
  // backend rows this is the first entry of `projectIds` and is kept
  // to drive the card chip + the activeVideo header copy.
  projectId: string;
  projectName: string;
  // All projects this video is tagged to. Populated for backend rows
  // from the `TransformationPickerPodcastVideoProject` join table;
  // omitted for mock data, where filter logic falls back to
  // `[projectId]`.
  projectIds?: string[];
  // Full project tags ({ id, name }) for the rows that carry them.
  // Drives the per-card project chips, mirroring the Manage Podcast
  // Videos dialog cards.
  projects?: { id: string; name: string }[];
  // Actual width/height ratio of the thumbnail image, captured by
  // `PodcastVideoCard` on image load and forwarded to the player so
  // landscape Facebook reels (and other off-format clips) don't get
  // letterboxed into the provider's default portrait container.
  naturalAspect?: number;
  status?: 'pending' | 'verified' | 'rejected';
}

// ---------------------------------------------------------------------------
// Backend record -> PodcastVideoItem
//
// Maps the API response shape from `api.listPodcastVideos()` to the
// internal `PodcastVideoItem` shape the card/player components
// already consume. Several fields aren't persisted to the DB yet
// (title, description, duration, creator avatar) — we derive
// sensible fallbacks so the row still renders.
// ---------------------------------------------------------------------------

export function podcastRecordToVideoItem(record: PodcastVideoRecord): PodcastVideoItem {
  const source = sourceFromUrl(record.videoUrl) ?? 'youtube';
  const projects = record.projects ?? [];
  const primaryProject = projects[0] ?? null;

  // Prefer the OG/oEmbed title persisted at submit time. If the
  // upstream provider didn't return one, fall back to hostname +
  // path so each card still has a recognisable line.
  let derivedTitle = record.title?.trim() || record.videoUrl;
  if (!record.title) {
    try {
      const u = new URL(record.videoUrl);
      derivedTitle = `${u.hostname.replace(/^www\./, '')}${u.pathname}`;
    } catch {
      // Keep raw URL fallback.
    }
  }

  return {
    id: `backend-${record.id}`,
    title: derivedTitle,
    source,
    thumbnail: record.thumbnailUrl ?? null,
    streamUrl: record.videoUrl,
    views: record.views,
    durationSeconds: 0,
    // The card surfaces this as "Posted on …". For verified rows we
    // prefer the review timestamp so the public tab reflects when
    // the video became visible rather than when it was first
    // submitted. Falls back to the creation timestamp for rows that
    // somehow surface here pre-review (e.g. dev tooling).
    createdAt:
      record.reviewedAt ?? record.createdAt ?? new Date().toISOString(),
    // Prefer the joined User display fields; fall back to the CEA so
    // a missing User row still shows *something* on the card.
    creatorName: record.createdByName || record.createdBy,
    creatorAvatar: record.createdByPhoto ?? null,
    projectId: primaryProject?.id ?? '',
    projectName: primaryProject?.name ?? '',
    projectIds: projects.map((p) => p.id),
    projects: projects.map((p) => ({ id: p.id, name: p.name })),
    status: record.status,
  };
}


// ---------------------------------------------------------------------------
// Formatting helpers (replicated locally so the tab stays self-contained).
// ---------------------------------------------------------------------------

function formatViews(n: number): string {
  if (n < 1_000) return `${n} view${n === 1 ? '' : 's'}`;
  if (n < 1_000_000) {
    const k = n / 1_000;
    const out = k < 10 ? k.toFixed(1).replace(/\.0$/, '') : Math.round(k).toString();
    return `${out}K views`;
  }
  const m = n / 1_000_000;
  const out = m < 10 ? m.toFixed(1).replace(/\.0$/, '') : Math.round(m).toString();
  return `${out}M views`;
}

export function formatCreatedDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '';
  const s = Math.round(totalSeconds);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${minutes}:${pad(seconds)}`;
}

// Landscape for long-form YouTube + generic "other", portrait for the
// mobile-first social formats (TikTok, Instagram Reels). We use a 3/4
// portrait ratio rather than the native 9/16 so the cards don't tower
// over their landscape neighbours in a mixed grid.
function getAspectRatio(source: PodcastSource): string {
  // Native portrait sources keep their tall aspect (clipped to 3/4 so
  // they don't dominate the grid). YouTube remains landscape.
  return source === 'youtube' ? '16 / 9' : '3 / 4';
}

// True 9/16 used only inside the dialog player so the embed renders at
// the platform's native portrait aspect.
function getPlayerAspectRatio(source: PodcastSource): string {
  return source === 'youtube' ? '16 / 9' : '9 / 16';
}

// Map a raw URL to one of the known PodcastSource buckets. Used by
// the preview card to show the correct provider badge before the
// user actually submits.
export function sourceFromUrl(url: string): PodcastSource | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtu.be') return 'youtube';
    if (host === 'tiktok.com') return 'tiktok';
    if (host === 'instagram.com') return 'instagram';
    if (host === 'facebook.com' || host === 'm.facebook.com' || host === 'fb.watch') return 'facebook';
    if (host === 'xiaohongshu.com' || host === 'xhslink.com') return 'rednote';
  } catch {
    // fall through
  }
  return null;
}

export function getSourceMeta(source: PodcastSource): {
  label: string;
  color: string;
  icon: React.ReactNode;
} {
  switch (source) {
    case 'youtube':
      return { label: 'YouTube', color: '#FF0000', icon: <YouTubeIcon sx={{ fontSize: 14 }} /> };
    case 'tiktok':
      return { label: 'TikTok', color: '#000000', icon: <TikTokIcon sx={{ fontSize: 14 }} /> };
    case 'instagram':
      return { label: 'Instagram', color: '#E1306C', icon: <InstagramIcon sx={{ fontSize: 14 }} /> };
    case 'facebook':
      return { label: 'Facebook', color: '#1877F2', icon: <FacebookIcon sx={{ fontSize: 14 }} /> };
    case 'rednote':
      return { label: 'RedNote', color: '#FF2442', icon: <RedNoteIcon sx={{ fontSize: 14 }} /> };
  }
}

type StreamSource =
  | { kind: 'iframe'; src: string; title: string }
  | { kind: 'none' };

// Lightweight stand-in for `ReactPlayer.canPlay(url)`. Mirrors the
// same provider detection logic the embedded player relies on, so
// any URL we accept here will also play in the dialog. Swap the body
// for `return ReactPlayer.canPlay(url)` once `react-player` is added
// to the frontend dependency tree.
export function canPlayUrl(url: string): boolean {
  const raw = (url || '').trim();
  if (!raw) return false;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return false;
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
  const host = u.hostname.replace(/^www\./, '');

  // YouTube — watch / shortened / embed URLs.
  if (host === 'youtube.com' || host === 'm.youtube.com') {
    if (u.pathname === '/watch' && u.searchParams.get('v')) return true;
    if (u.pathname.startsWith('/embed/') && u.pathname.length > '/embed/'.length) return true;
    if (u.pathname.startsWith('/shorts/') && u.pathname.length > '/shorts/'.length) return true;
    return false;
  }
  if (host === 'youtu.be' && u.pathname.replace(/^\//, '').length > 0) return true;

  // TikTok — full video URL with numeric id.
  if (host === 'tiktok.com' && /\/video\/\d+/.test(u.pathname)) return true;

  // Instagram — reels (`/reel/<id>` or `/reels/<id>`), posts (`/p/<id>`),
  // and IGTV (`/tv/<id>`).
  if (host === 'instagram.com') {
    return /\/(reels?|p|tv)\/[\w-]+/.test(u.pathname);
  }

  // Facebook — reels (`/reel/<id>`), videos (`/<page>/videos/<id>` or
  // `/watch/?v=<id>`), and the `fb.watch/<token>` short links.
  if (host === 'facebook.com' || host === 'm.facebook.com') {
    if (/^\/reel\/\d+/.test(u.pathname)) return true;
    if (/^\/[^/]+\/videos\/\S+/.test(u.pathname)) return true;
    if (u.pathname === '/watch' || u.pathname === '/watch/') {
      return Boolean(u.searchParams.get('v'));
    }
    if (/^\/video\.php/.test(u.pathname) && u.searchParams.get('v')) return true;
    return false;
  }
  if (host === 'fb.watch' && u.pathname.replace(/^\//, '').length > 0) return true;

  // RedNote (小红书) — public note pages.
  if (host === 'xiaohongshu.com') {
    return (
      /^\/explore\/[\w-]+/.test(u.pathname) ||
      /^\/discovery\/item\/[\w-]+/.test(u.pathname) ||
      /^\/user\/profile\/[\w-]+\/[\w-]+/.test(u.pathname)
    );
  }
  if (host === 'xhslink.com' && u.pathname.replace(/^\//, '').length > 0) return true;

  return false;
}

function extractYouTubeVideoId(rawUrl: string): string | null {
  try {
    const u = new URL(rawUrl.trim());
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '').split('/')[0];
      return id || null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
      if (u.pathname === '/watch') return u.searchParams.get('v');
      const embed = u.pathname.match(/^\/embed\/([^/?#]+)/);
      if (embed) return embed[1];
      const shorts = u.pathname.match(/^\/shorts\/([^/?#]+)/);
      if (shorts) return shorts[1];
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * YouTube's embedded player requires a valid Referer / origin for
 * identification (see YouTube API Minimum Functionality). Without
 * `origin`, some networks/devices hit bot checks ("Sign in to confirm
 * you're not a bot") even when the same video plays fine elsewhere.
 */
function buildYouTubeEmbedSrc(videoId: string): string {
  const params = new URLSearchParams({
    autoplay: '1',
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
    enablejsapi: '1',
  });
  if (typeof window !== 'undefined' && window.location?.origin) {
    params.set('origin', window.location.origin);
    params.set('widget_referrer', window.location.origin);
  }
  return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${params.toString()}`;
}

function resolveStreamSource(video: PodcastVideoItem): StreamSource {
  const raw = (video.streamUrl || '').trim();
  if (!raw) return { kind: 'none' };

  // YouTube — normalise to /embed/ with origin identification params.
  const youtubeId = extractYouTubeVideoId(raw);
  if (youtubeId) {
    return {
      kind: 'iframe',
      src: buildYouTubeEmbedSrc(youtubeId),
      title: video.title,
    };
  }

  try {
    const u = new URL(raw);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'tiktok.com') {
      // TikTok's embed player. The older /embed/v2/<id> page answers
      // "503 overload-protect triggered" for our traffic.
      const match = u.pathname.match(/\/video\/(\d+)/);
      if (match) {
        const params = new URLSearchParams({ autoplay: '1', loop: '0', controls: '1', timestamp: '0' });
        return {
          kind: 'iframe',
          src: `https://www.tiktok.com/player/v1/${match[1]}?${params.toString()}`,
          title: video.title,
        };
      }
    }
    if (host === 'instagram.com') {
      // Normalise to `https://www.instagram.com/<type>/<id>/embed`.
      // Instagram accepts /reel/<id>/embed but NOT /reels/<id>/embed,
      // so we collapse the plural variant. We also drop any query
      // string (`?hl=en` etc.) because it breaks the embed page.
      const match = u.pathname.match(/^\/(reels?|p|tv)\/([\w-]+)/);
      if (match) {
        const type = match[1] === 'reels' ? 'reel' : match[1];
        const id = match[2];
        return {
          kind: 'iframe',
          src: `https://www.instagram.com/${type}/${id}/embed`,
          title: video.title,
        };
      }
    }
    if (host === 'xiaohongshu.com' || host === 'xhslink.com') {
      // RedNote (小红书) has no public embed iframe — their pages
      // block third-party framing via X-Frame-Options. We still set
      // up an iframe pointing at the canonical note URL so the user
      // at least sees RedNote's own page if they're authenticated,
      // and the metadata-preview thumbnail provides the actual
      // visual when embedding fails.
      return { kind: 'iframe', src: raw, title: video.title };
    }
    if (
      host === 'facebook.com' ||
      host === 'm.facebook.com' ||
      host === 'fb.watch'
    ) {
      // Facebook's public video plugin renders reels, watch videos, and
      // page video posts without requiring an access token. We pass the
      // raw URL through as the `href` argument and let FB resolve it.
      const params = new URLSearchParams({
        href: raw,
        show_text: 'false',
        autoplay: 'true',
        mute: '0',
      });
      return {
        kind: 'iframe',
        src: `https://www.facebook.com/plugins/video.php?${params.toString()}`,
        title: video.title,
      };
    }
  } catch {
    // fall through
  }

  return { kind: 'none' };
}

// ---------------------------------------------------------------------------
// PodcastVideoCard
// ---------------------------------------------------------------------------

interface PodcastVideoCardProps {
  video: PodcastVideoItem;
  onPlay: (video: PodcastVideoItem) => void;
}

export function PodcastVideoCard({ video, onPlay }: PodcastVideoCardProps) {
  const durationLabel = formatDuration(video.durationSeconds);
  const sourceMeta = getSourceMeta(video.source);
  const [shareOpen, setShareOpen] = useState(false);

  // Some providers (notably Facebook) host both portrait reels and
  // landscape watch videos under the same URL pattern, so a static
  // provider-default aspect ratio causes huge black bands in the
  // card. We capture the thumbnail's natural aspect on image load
  // and use it as the source of truth when available.
  const [naturalAspect, setNaturalAspect] = useState<number | null>(
    video.naturalAspect ?? null,
  );
  const aspect = naturalAspect
    ? `${naturalAspect}`
    : getAspectRatio(video.source);

  return (
    <>
    <Card
      sx={{
        width: '100%',
        bgcolor: 'var(--color-surface)',
        color: 'var(--color-text)',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: 'var(--color-shadow-card)',
        border: '1px solid var(--color-border)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 200ms ease, box-shadow 200ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
        },
      }}
    >
      {/* Thumbnail — native aspect ratio per source. */}
      <Box
        component="button"
        type="button"
        onClick={() =>
          onPlay({
            ...video,
            // Forward the discovered aspect so the player dialog
            // sizes itself to match the actual video instead of the
            // provider default.
            naturalAspect: naturalAspect ?? video.naturalAspect,
          })
        }
        aria-label={`Play ${video.title}`}
        sx={{
          all: 'unset',
          position: 'relative',
          display: 'block',
          width: '100%',
          aspectRatio: aspect,
          overflow: 'hidden',
          cursor: 'pointer',
          bgcolor: 'var(--color-surface-elevated)',
          '&:hover .podcast-play-overlay': { opacity: 1 },
          '&:focus-visible': {
            outline: '3px solid var(--color-accent)',
            outlineOffset: '-3px',
          },
        }}
      >
        {video.thumbnail ? (
          <Box
            component="img"
            data-magnify-image="podcast-thumbnail"
            src={video.thumbnail}
            alt=""
            onLoad={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setNaturalAspect(img.naturalWidth / img.naturalHeight);
              }
            }}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text-muted)',
              fontWeight: 700,
            }}
          >
            {video.title}
          </Box>
        )}

        {/* Source badge — top-left pill so each card visually advertises
            its platform of origin. */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.85,
            height: 22,
            borderRadius: 1,
            bgcolor: sourceMeta.color,
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '0.02em',
          }}
        >
          {sourceMeta.icon}
          {sourceMeta.label}
        </Box>

        {/* Hover play overlay */}
        <Box
          className="podcast-play-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.18)',
            opacity: 0,
            transition: 'opacity 180ms ease',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PlayArrowRoundedIcon sx={{ fontSize: 36 }} />
          </Box>
        </Box>

        {durationLabel && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              px: 0.85,
              height: 22,
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 1,
              bgcolor: 'rgba(0, 0, 0, 0.75)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.72rem',
              letterSpacing: '0.02em',
            }}
          >
            {durationLabel}
          </Box>
        )}
      </Box>

      {/* Meta row — title, then creator (avatar + name), then views/date. */}
      <Box sx={{ p: { xs: 1.25, sm: 1.5 } }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: 'var(--color-text-strong)',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
          title={video.title}
        >
          {video.title}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
          <Avatar
            src={video.creatorAvatar || undefined}
            alt={video.creatorName}
            sx={{ width: 24, height: 24, fontSize: '0.7rem' }}
          >
            {video.creatorName.charAt(0)}
          </Avatar>
          <Typography
            variant="caption"
            sx={{
              color: 'var(--color-text-strong)',
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={video.creatorName}
          >
            {video.creatorName}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 0.5 }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'var(--color-text-muted)',
              fontWeight: 500,
            }}
          >
            {formatCreatedDate(video.createdAt) || ''}
          </Typography>

          {(!video.status || video.status === 'verified') && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setShareOpen(true);
              }}
              sx={{
                color: 'var(--color-text-muted)',
                p: 0.5,
                '&:hover': {
                  color: 'var(--color-accent)',
                  bgcolor: 'var(--color-accent-soft)',
                },
              }}
              aria-label="Share video"
            >
              <ShareIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Stack>

        {/* Project tags — mirrors the Manage Podcast Videos dialog cards
            so a viewer can see which project(s) a clip is tagged to. */}
        {video.projects && video.projects.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {video.projects.map((p) => (
              <Chip key={p.id} size="small" label={p.name} sx={{ height: 20 }} />
            ))}
          </Box>
        )}
      </Box>
    </Card>
    <ShareVideoDialog
      open={shareOpen}
      onClose={() => setShareOpen(false)}
      video={video}
    />
    </>
  );
}

// ---------------------------------------------------------------------------
// ShareVideoDialog
// ---------------------------------------------------------------------------

interface ShareVideoDialogProps {
  open: boolean;
  onClose: () => void;
  video: PodcastVideoItem;
}

export function ShareVideoDialog({ open, onClose, video }: ShareVideoDialogProps) {
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);

  useEffect(() => {
    if (open) {
      setShared(false);
    }
  }, [open]);

  const triggerShare = async () => {
    if (shared) return;
    setShared(true);
    try {
      const numericId = parseInt(video.id.replace('backend-', ''), 10);
      if (!isNaN(numericId)) {
        await api.incrementPodcastVideoShare(numericId);
      }
    } catch (err) {
      console.error('Failed to increment share count:', err);
    }
  };

  const shareText = `Check out this video: ${video.title} - ${video.streamUrl}`;
  const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
  const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(video.streamUrl)}&text=${encodeURIComponent(video.title)}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(video.streamUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      triggerShare();
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text)',
          borderRadius: 2,
          p: 1,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid var(--color-border-strong)' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
          Share Video
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'var(--color-text-muted)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--color-text-strong)', lineHeight: 1.3 }}>
          {video.title}
        </Typography>

        {/* Copy Link field */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            borderRadius: 1.5,
            border: '1px solid var(--color-border)',
            bgcolor: 'var(--color-bg)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'var(--color-text-muted)',
              fontFamily: 'monospace',
            }}
          >
            {video.streamUrl}
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={handleCopy}
            sx={{
              minWidth: 70,
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: copied ? 'success.main' : 'var(--color-accent)',
              color: '#fff',
              '&:hover': {
                bgcolor: copied ? 'success.dark' : 'var(--color-accent-strong)',
              },
            }}
          >
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </Box>

        {/* Social Platforms Row */}
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 1 }}>
          <Button
            component="a"
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            onClick={triggerShare}
            startIcon={<WhatsAppIcon sx={{ color: '#25D366' }} />}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              borderColor: 'rgba(37, 211, 102, 0.3)',
              color: 'var(--color-text)',
              '&:hover': {
                borderColor: '#25D366',
                bgcolor: 'rgba(37, 211, 102, 0.08)',
              },
            }}
          >
            WhatsApp
          </Button>
          <Button
            component="a"
            href={telegramUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            onClick={triggerShare}
            startIcon={<TelegramIcon sx={{ color: '#0088cc' }} />}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              borderColor: 'rgba(0, 136, 204, 0.3)',
              color: 'var(--color-text)',
              '&:hover': {
                borderColor: '#0088cc',
                bgcolor: 'rgba(0, 136, 204, 0.08)',
              },
            }}
          >
            Telegram
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// PodcastVideoPlayer
// ---------------------------------------------------------------------------

function PodcastVideoPlayer({ video }: { video: PodcastVideoItem }) {
  const source = resolveStreamSource(video);
  // Prefer the actual thumbnail aspect captured by the card. Falls
  // back to the provider default for sources whose embed dimensions
  // are well-known (YouTube 16:9, TikTok 9:16, etc.).
  const aspect = video.naturalAspect
    ? `${video.naturalAspect}`
    : getPlayerAspectRatio(video.source);
  // Padding-top trick keeps the aspect ratio stable across browsers.
  const paddingTop = video.naturalAspect
    ? `${(1 / video.naturalAspect) * 100}%`
    : aspect === '9 / 16'
      ? '177.78%'
      : '56.25%';

  const responsiveBoxSx = {
    position: 'relative' as const,
    display: 'block',
    width: '100%',
    height: 0,
    paddingTop,
    overflow: 'hidden',
    bgcolor: '#000',
  };
  const responsiveItemSx = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 0,
    display: 'block',
  };

  const youtubeId =
    video.source === 'youtube' ? extractYouTubeVideoId(video.streamUrl) : null;
  const youtubeWatchUrl = youtubeId
    ? `https://www.youtube.com/watch?v=${encodeURIComponent(youtubeId)}`
    : null;

  if (source.kind === 'iframe') {
    return (
      <Box>
        <Box sx={responsiveBoxSx}>
          <Box
            component="iframe"
            src={source.src}
            title={source.title}
            allow="autoplay; encrypted-media; picture-in-picture; accelerometer; gyroscope; clipboard-write; fullscreen"
            allowFullScreen
            // YouTube requires a usable Referer; their docs recommend
            // strict-origin-when-cross-origin (not "origin" alone).
            referrerPolicy="strict-origin-when-cross-origin"
            sx={responsiveItemSx}
          />
        </Box>
        {youtubeWatchUrl && (
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: 'rgba(0,0,0,0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.72)', textAlign: 'center' }}
            >
              Can&apos;t play here?{' '}
              <Box
                component="a"
                href={youtubeWatchUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                  textDecoration: 'underline',
                  '&:hover': { color: '#FF0000' },
                }}
              >
                Open on YouTube
              </Box>
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        aspectRatio: aspect,
        bgcolor: '#000',
        color: 'rgba(255, 255, 255, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
      }}
    >
      <Typography variant="body2">Streaming source unavailable for this video.</Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// AddVideoPreview
//
// Renders inside the "Add Podcast Video" dialog underneath the URL
// field. Shows the provider thumbnail (from the backend OG scrape),
// the video title, and the source badge so the user can confirm the
// pasted link points at the video they meant before submitting.
// ---------------------------------------------------------------------------

interface AddVideoPreviewProps {
  url: string;
  source: PodcastSource;
  meta: VideoMetadata | null;
  loading: boolean;
  error: string | null;
}

export function AddVideoPreview({ url, source, meta, loading, error }: AddVideoPreviewProps) {
  const sourceMeta = getSourceMeta(source);
  const providerAspect = getAspectRatio(source);
  const thumbnail = meta?.image || null;
  const title = meta?.title || null;
  const siteName = meta?.siteName || null;

  // Actual thumbnail aspect ratio measured from the loaded image, so
  // a landscape OG image renders landscape and a portrait one renders
  // portrait, regardless of the provider's default orientation. We
  // fall back to the provider preset while the image is still
  // downloading (and for the placeholder state) to avoid layout
  // shift when the URL is first pasted.
  const [naturalAspect, setNaturalAspect] = useState<number | null>(null);

  // Reset the measured aspect whenever the thumbnail URL changes so
  // pasting a different link doesn't briefly render the new image at
  // the previous image's aspect ratio.
  useEffect(() => {
    setNaturalAspect(null);
  }, [thumbnail]);

  const effectiveAspect = naturalAspect ?? providerAspect;

  return (
    <Box
      sx={{
        mt: 1,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid rgba(var(--color-text-rgb), 0.1)',
        bgcolor: 'var(--color-surface-elevated)',
      }}
    >
      {/* Thumbnail area. The container's aspect ratio mirrors the
          actual image once it loads so landscape/portrait/square
          thumbnails all render at their true proportions; until then
          we use the provider preset to keep the layout stable. */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          // Cap the portrait preview so a 9/16 thumbnail does not
          // dominate the dialog vertically. Landscape thumbnails are
          // naturally shorter so this cap is effectively a no-op for
          // them.
          maxHeight: 360,
          aspectRatio: effectiveAspect,
          // Center the image inside the cap so portrait thumbnails
          // that hit `maxHeight` letterbox cleanly instead of
          // stretching.
          mx: 'auto',
          bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {thumbnail ? (
          <Box
            component="img"
            src={thumbnail}
            alt={title || 'Video thumbnail'}
            onLoad={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setNaturalAspect(img.naturalWidth / img.naturalHeight);
              }
            }}
            sx={{
              // `contain` keeps the full image visible at its true
              // aspect ratio. Once the container matches the natural
              // aspect this is equivalent to `cover`, but during the
              // brief window before `onLoad` fires it prevents the
              // provider-preset container from cropping the image.
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        ) : (
          <Stack alignItems="center" spacing={1} sx={{ color: 'var(--color-text-muted)' }}>
            {loading ? (
              <>
                <CircularProgress size={28} thickness={4} sx={{ color: 'var(--color-accent)' }} />
                <Typography variant="caption">Loading preview…</Typography>
              </>
            ) : (
              <>
                <PlayArrowRoundedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                <Typography variant="caption" sx={{ px: 2, textAlign: 'center' }}>
                  {error
                    ? 'Preview unavailable for this link'
                    : 'No thumbnail provided by the source'}
                </Typography>
              </>
            )}
          </Stack>
        )}

        {/* Source badge — same visual language as the feed cards. */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 999,
            bgcolor: sourceMeta.color,
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1.4,
          }}
        >
          {sourceMeta.icon}
          {sourceMeta.label}
        </Box>
      </Box>

      {/* Metadata strip below the thumbnail. */}
      <Box sx={{ px: 1.5, py: 1.25 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: 'var(--color-text-strong)',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            mb: 0.25,
            wordBreak: 'break-word',
          }}
        >
          {title || (loading ? 'Loading title…' : new URL(url).hostname)}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'var(--color-text-muted)',
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {siteName || new URL(url).hostname}
        </Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// PodcastVideosTab — main component
// ---------------------------------------------------------------------------

export default function PodcastVideosTab(props: PodcastVideosTabProps) {
  const { isAdmin = false, isAccelerator = false, isVideoUploader = false } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchQuery, setSearchQuery] = useState<string>('');
  // Multi-select project filter, mirroring the "Tag to project(s)"
  // dropdown in the Add dialog. Empty array = no filter (show all).
  const [filterProjectIds, setFilterProjectIds] = useState<string[]>([]);
  const [activeVideo, setActiveVideo] = useState<PodcastVideoItem | null>(null);
  const [playerShareOpen, setPlayerShareOpen] = useState(false);

  // Real podcast videos pulled from the backend. We keep these in a
  // separate state slice from the mocks so a failed reload doesn't
  // wipe the locally-rendered preview videos.
  const [backendVideos, setBackendVideos] = useState<PodcastVideoItem[]>([]);
  const [backendVideosLoading, setBackendVideosLoading] = useState<boolean>(false);
  const [backendVideosError, setBackendVideosError] = useState<string | null>(null);

  // "Add Video" dialog state. `urlTouched` lets us suppress the error
  // message until the user has actually interacted with the field, so
  // the dialog doesn't open with a red error already visible.
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [urlInput, setUrlInput] = useState<string>('');
  const [urlTouched, setUrlTouched] = useState<boolean>(false);
  const trimmedUrl = urlInput.trim();
  const urlValid = trimmedUrl.length > 0 && canPlayUrl(trimmedUrl);
  const previewSource = urlValid ? sourceFromUrl(trimmedUrl) : null;
  const urlShowError = urlTouched && trimmedUrl.length > 0 && !urlValid;

  // Preview metadata fetched from the backend Open Graph scraper. We
  // only fire the request once the URL passes the local canPlay
  // check, and we cancel any in-flight request as soon as the URL
  // changes again — otherwise rapid typing would race responses.
  const [previewMeta, setPreviewMeta] = useState<VideoMetadata | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Submission state for the create-podcast-video API call. We keep
  // these separate from preview state so the user can see preview
  // errors and submit errors at the same time without one wiping the
  // other.
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Success confirmation dialog shown after a successful submit so the
  // user knows their video is awaiting admin review before it becomes
  // visible on the public tab.
  const [submitSuccessOpen, setSubmitSuccessOpen] = useState<boolean>(false);

  // Project tagging is mandatory before submit — podcast videos must
  // be linked to at least one project. We lazy-load the project list
  // the first time the dialog opens so we don't fetch on tab mount.
  // Shared between the project filter dropdown and the Add-dialog
  // "Tag to project(s)" picker. Loaded once on tab mount so the
  // filter is interactive without the user having to open the
  // dialog first.
  const [projectOptions, setProjectOptions] = useState<TopViewingSitePlan[]>([]);
  const [projectOptionsLoading, setProjectOptionsLoading] = useState<boolean>(false);
  const [projectOptionsError, setProjectOptionsError] = useState<string | null>(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);

  useEffect(() => {
    if (!addOpen || !urlValid) {
      setPreviewMeta(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }
    const controller = new AbortController();
    // Debounce so we do not hammer the backend while the user is
    // still typing the tail end of a URL.
    const timer = window.setTimeout(() => {
      setPreviewLoading(true);
      setPreviewError(null);
      api
        .fetchVideoMetadata(trimmedUrl, controller.signal)
        .then((meta) => {
          setPreviewMeta(meta);
          setPreviewLoading(false);
        })
        .catch((err: unknown) => {
          if ((err as Error)?.name === 'AbortError') return;
          setPreviewMeta(null);
          setPreviewError(
            err instanceof Error ? err.message : 'Failed to load preview',
          );
          setPreviewLoading(false);
        });
    }, 400);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [addOpen, urlValid, trimmedUrl]);

  const handleOpenAddDialog = () => {
    setUrlInput('');
    setUrlTouched(false);
    setPreviewMeta(null);
    setPreviewError(null);
    setPreviewLoading(false);
    setSubmitError(null);
    setSubmitting(false);
    setSelectedProjectIds([]);
    setAddOpen(true);
  };
  const handleCloseAddDialog = () => {
    if (submitting) return; // don't close mid-flight
    setAddOpen(false);
  };
  const handleSubmitAddDialog = async () => {
    if (
      !urlValid ||
      !previewSource ||
      submitting ||
      selectedProjectIds.length === 0 ||
      selectedProjectIds.length > MAX_PROJECT_TAGS
    ) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.createPodcastVideo({
        videoUrl: trimmedUrl,
        videoSource: getSourceMeta(previewSource).label,
        thumbnailUrl: previewMeta?.image ?? null,
        title: previewMeta?.title ?? null,
        projectIds: selectedProjectIds,
      });
      setAddOpen(false);
      // Surface a success confirmation so the submitter understands
      // the video is queued for admin review rather than published
      // immediately (it won't appear on the public tab until then).
      setSubmitSuccessOpen(true);
      // Refresh the feed so the new video appears immediately. We
      // fire-and-forget so the dialog still closes promptly even if
      // the reload takes a moment.
      loadBackendVideos();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to add podcast video',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Load the project list on tab mount so both the filter dropdown
  // and the Add-dialog tag picker have data immediately. The guard
  // ref dedupes concurrent loads — putting `projectOptions` /
  // `projectOptionsLoading` in the deps would re-run the effect as
  // soon as we flip loading on, marking the previous closure as
  // cancelled and stranding `loading: true` forever.
  const projectsLoadStartedRef = useRef<boolean>(false);
  const loadProjectOptions = useCallback(async () => {
    if (projectsLoadStartedRef.current) return;
    projectsLoadStartedRef.current = true;
    setProjectOptionsLoading(true);
    setProjectOptionsError(null);
    try {
      const rows = await api.getTopViewingSitePlans();
      setProjectOptions(rows);
    } catch (err) {
      setProjectOptionsError(
        err instanceof Error ? err.message : 'Failed to load projects',
      );
      // Allow a retry on next mount / dialog open if the first load failed.
      projectsLoadStartedRef.current = false;
    } finally {
      setProjectOptionsLoading(false);
    }
  }, []);
  useEffect(() => {
    loadProjectOptions();
  }, [loadProjectOptions]);

  // Load the backend podcast videos. Converted to `PodcastVideoItem`
  // so the existing card / player components can render them with no
  // changes. The mock videos remain rendered alongside.
  const loadBackendVideos = useCallback(async () => {
    setBackendVideosLoading(true);
    setBackendVideosError(null);
    try {
      const rows = await api.listPodcastVideos();
      setBackendVideos(rows.map(podcastRecordToVideoItem));
    } catch (err) {
      setBackendVideosError(
        err instanceof Error ? err.message : 'Failed to load podcast videos',
      );
    } finally {
      setBackendVideosLoading(false);
    }
  }, []);
  useEffect(() => {
    loadBackendVideos();
  }, [loadBackendVideos]);

  // Project-lead gate for the "Add Video" button. Admins, accelerators, and
  // VIDEO-UPLOADER always pass; everyone else has to be a responsible party
  // on at least one verifiable project (Head / Deputy / lead-less IC) — which
  // is the same population that surfaces under the Project Verification
  // dialog's "Project Leads" tab. We probe the lightweight
  // `/my-projects` endpoint (returns only the caller's memberships)
  // so we don't need a new endpoint or admin-only access.
  const [isProjectLead, setIsProjectLead] = useState<boolean>(false);
  useEffect(() => {
    if (isAdmin || isAccelerator || isVideoUploader) return;
    let cancelled = false;
    (async () => {
      try {
        const { projects } = await api.getMyProjectVerifications();
        if (!cancelled) setIsProjectLead((projects?.length ?? 0) > 0);
      } catch {
        if (!cancelled) setIsProjectLead(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, isAccelerator, isVideoUploader]);
  const canAddVideo = isAdmin || isAccelerator || isVideoUploader || isProjectLead;

  // Back-to-top: SuperPickerApp scrolls its main content area inside a
  // dedicated overflow:auto container rather than on the window, so we
  // walk up from our root node on mount to find that container and
  // attach both the visibility listener and the scrollTo call to it.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLElement | Window | null>(null);
  const [showBackToTop, setShowBackToTop] = useState<boolean>(false);

  useEffect(() => {
    const findScrollContainer = (el: HTMLElement | null): HTMLElement | Window => {
      let node: HTMLElement | null = el?.parentElement ?? null;
      while (node) {
        const style = window.getComputedStyle(node);
        const oy = style.overflowY;
        if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight) {
          return node;
        }
        node = node.parentElement;
      }
      return window;
    };
    const container = findScrollContainer(rootRef.current);
    scrollContainerRef.current = container;

    const getScrollTop = () =>
      container === window
        ? window.scrollY || document.documentElement.scrollTop
        : (container as HTMLElement).scrollTop;

    const onScroll = () => {
      setShowBackToTop(getScrollTop() > 320);
    };
    onScroll();
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
    };
  }, []);

  const handleScrollToTop = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (container === window) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      (container as HTMLElement).scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const filteredVideos = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const hasProjectFilter = filterProjectIds.length > 0;
    const projectFilterSet = new Set(filterProjectIds);
    return backendVideos.filter((v) => {
      if (hasProjectFilter) {
        const ids = v.projectIds && v.projectIds.length > 0 ? v.projectIds : [v.projectId];
        if (!ids.some((id) => projectFilterSet.has(id))) return false;
      }
      if (!q) return true;
      return (
        v.title.toLowerCase().includes(q) ||
        v.creatorName.toLowerCase().includes(q) ||
        v.projectName.toLowerCase().includes(q)
      );
    });
  }, [backendVideos, searchQuery, filterProjectIds]);

  const handleClearSearch = () => setSearchQuery('');

  return (
    <Box
      ref={rootRef}
      sx={{
        width: '100%',
        // Pull the tab content flush against the scroll container's
        // padding so the sticky bar below can sit at the true top of
        // the viewport — without this offset the parent's p:2/3 leaves
        // a visible gap above the pinned search field.
        mt: { xs: -2, md: -3 },
        mx: { xs: -2, md: -3 },
        px: { xs: 2, md: 3 },
      }}
    >
      {/* Non-sticky title block — scrolls away normally. */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ pt: { xs: 2, md: 3 }, mb: 2 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: 'var(--color-text-strong)', mb: 0.5 }}
          >
            Podcast Videos (Customer Engagement)
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
            Project-related podcasts and shorts.
          </Typography>
        </Box>

        {canAddVideo && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: 'var(--color-accent)',
              color: '#fff',
              '&:hover': {
                bgcolor: 'var(--color-accent-strong, var(--color-accent))',
              },
            }}
          >
            Add Video
          </Button>
        )}
      </Stack>

      {/* Sticky bar — only the search input and project filter pin to
          the top. Extends edge-to-edge of the scroll container via
          negative margins so its background fully occludes content
          scrolling beneath it. */}
      <Box
        sx={{
          position: 'sticky',
          // Pin the bar's top edge against the very top of the scroll
          // viewport. The negative top + matching pt cancels out the
          // parent <main>'s p:{xs:2, md:3} so the bar's background
          // extends right up to the AppHeader with no visible gap,
          // while the search field itself still has breathing room
          // below (provided by the bar's pt).
          top: { xs: -16, md: -24 },
          zIndex: (theme) => theme.zIndex.appBar - 1,
          mx: { xs: -2, md: -3 },
          px: { xs: 2, md: 3 },
          pt: { xs: 2, md: 3 },
          pb: 1.5,
          bgcolor: 'var(--color-bg)',
          borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
          mb: 3,
        }}
      >
        <Stack direction="column" spacing={1.5} alignItems="stretch">
              <TextField
                size="small"
                placeholder="Search by title or creator..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'var(--color-surface-elevated)',
                    borderRadius: 2,
                    '& fieldset': { borderColor: 'rgba(var(--color-text-rgb), 0.12)' },
                    '&:hover fieldset': { borderColor: 'var(--color-accent)' },
                    '&.Mui-focused fieldset': { borderColor: 'var(--color-accent)' },
                  },
                  '& .MuiInputBase-input': {
                    color: 'var(--color-text)',
                    fontWeight: 500,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'var(--color-text-muted)', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        sx={{ color: 'var(--color-text-muted)' }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              {/* Multi-select project filter — mirrors the Add
                  dialog's "Tag to project(s)" picker. Empty value =
                  no filter. Matches against the project IDs returned
                  by `listPodcastVideos`, so once the join table is
                  populated this filter narrows the feed correctly. */}
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                loading={projectOptionsLoading}
                options={projectOptions}
                value={filterProjectIds
                  .map((id) => projectOptions.find((p) => p.projectId === id))
                  .filter((p): p is TopViewingSitePlan => Boolean(p))}
                onChange={(_, value) =>
                  setFilterProjectIds(value.map((p) => p.projectId))
                }
                getOptionLabel={(opt) => opt.projectName || opt.projectId}
                isOptionEqualToValue={(option, value) =>
                  option.projectId === value.projectId
                }
                sx={{ width: '100%' }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                    label="Project"
                    placeholder={
                      filterProjectIds.length === 0 ? 'All projects' : ''
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'var(--color-surface-elevated)',
                        borderRadius: 2,
                        '& fieldset': { borderColor: 'rgba(var(--color-text-rgb), 0.12)' },
                        '&:hover fieldset': { borderColor: 'var(--color-accent)' },
                        '&.Mui-focused fieldset': { borderColor: 'var(--color-accent)' },
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((opt, idx) => (
                    <Chip
                      {...getTagProps({ index: idx })}
                      key={opt.projectId}
                      label={opt.projectName || opt.projectId}
                      size="small"
                    />
                  ))
                }
              />
            </Stack>
      </Box>

      {/* Results grid */}
      {filteredVideos.length === 0 ? (
        <Card
          sx={{
            py: 8,
            textAlign: 'center',
            bgcolor: 'var(--color-surface)',
            border: '1px solid rgba(var(--color-text-rgb), 0.08)',
            borderRadius: 2,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ color: 'var(--color-text-strong)', fontWeight: 600, mb: 1 }}
          >
            No podcast videos found
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'var(--color-text-muted)', maxWidth: 400, mx: 'auto', px: 2 }}
          >
            Try adjusting your search query or selecting a different project filter.
          </Typography>
        </Card>
      ) : (
        <Box
          sx={{
            // CSS multi-column layout handles mixed-aspect-ratio cards
            // gracefully — cards flow into columns and short cards
            // automatically backfill gaps under taller ones, so we
            // never get the cavernous whitespace that a fixed CSS grid
            // produces when landscape cards sit next to portraits.
            columnCount: { xs: 2, sm: 3, md: 4, lg: 5 },
            columnGap: { xs: 2, sm: 2, md: 2 },
            p: 0.5,
            '& > *': {
              breakInside: 'avoid',
              WebkitColumnBreakInside: 'avoid',
              pageBreakInside: 'avoid',
              mb: { xs: 1.5, sm: 2, md: 2.5 },
              display: 'block',
            },
          }}
        >
          {filteredVideos.map((video) => (
            <PodcastVideoCard
              key={video.id}
              video={video}
              onPlay={async (v) => {
                setActiveVideo(v);
                try {
                  const numericId = parseInt(v.id.replace('backend-', ''), 10);
                  if (!isNaN(numericId)) {
                    await api.incrementPodcastVideoView(numericId);
                    setBackendVideos((prev) =>
                      prev.map((item) =>
                        item.id === v.id ? { ...item, views: item.views + 1 } : item
                      )
                    );
                  }
                } catch (err) {
                  console.error('Failed to increment view count:', err);
                }
              }}
            />
          ))}
        </Box>
      )}

      {/* Player dialog */}
      <Dialog
        open={Boolean(activeVideo)}
        onClose={() => setActiveVideo(null)}
        maxWidth={
          activeVideo &&
          // Use `xs` for portrait videos (whether detected from the
          // thumbnail or implied by the provider default) so the
          // dialog doesn't stretch a tall 9:16 reel to a wide canvas.
          (activeVideo.naturalAspect
            ? activeVideo.naturalAspect < 1
            : getPlayerAspectRatio(activeVideo.source) === '9 / 16')
            ? 'xs'
            : 'md'
        }
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text)',
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
            boxShadow: 'var(--color-shadow-card)',
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setActiveVideo(null)}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 10,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.75)' },
            }}
          >
            <CloseIcon />
          </IconButton>

          {activeVideo && (
            <Box key={activeVideo.id}>
              <PodcastVideoPlayer video={activeVideo} />

              <Box sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: 'var(--color-text-strong)',
                    lineHeight: 1.4,
                    mb: 1.5,
                  }}
                >
                  {activeVideo.title}
                </Typography>

                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <Avatar
                    src={activeVideo.creatorAvatar || undefined}
                    alt={activeVideo.creatorName}
                    sx={{ width: 36, height: 36 }}
                  >
                    {activeVideo.creatorName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}
                    >
                      {activeVideo.creatorName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'var(--color-text-muted)', fontWeight: 500 }}
                    >
                      {activeVideo.projectName}
                    </Typography>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    pt: 1.5,
                    borderTop: '1px solid var(--color-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: 'var(--color-text-muted)', fontWeight: 500 }}
                  >
                    {formatCreatedDate(activeVideo.createdAt)
                      ? `Posted on ${formatCreatedDate(activeVideo.createdAt)} • `
                      : ''}
                    {getSourceMeta(activeVideo.source).label}
                  </Typography>

                  {(!activeVideo.status || activeVideo.status === 'verified') && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ShareIcon />}
                      onClick={() => setPlayerShareOpen(true)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 1.5,
                        borderColor: 'var(--color-border-strong)',
                        color: 'var(--color-text-strong)',
                        '&:hover': {
                          borderColor: 'var(--color-accent)',
                          bgcolor: 'var(--color-accent-soft)',
                          color: 'var(--color-accent-strong)',
                        },
                      }}
                    >
                      Share
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {activeVideo && (
        <ShareVideoDialog
          open={playerShareOpen}
          onClose={() => setPlayerShareOpen(false)}
          video={activeVideo}
        />
      )}

      {/* Add Video dialog — accepts a video URL from any supported
          provider (YouTube, Facebook, Instagram, TikTok, RedNote)
          and validates it via the same canPlay logic the embedded
          player uses, so anything we accept here is guaranteed to
          play. Submit stays disabled until the URL passes. */}
      <Dialog
        open={addOpen}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text)',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: 'var(--color-text-strong)',
            pb: 1,
          }}
        >
          Add Podcast Video
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ color: 'var(--color-text-muted)', mb: 1 }}
          >
            Supported media platforms:
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            {(
              ['youtube', 'facebook', 'instagram', 'tiktok', 'rednote'] as const
            ).map((s) => {
              const meta = getSourceMeta(s);
              return (
                <Box
                  key={s}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.4,
                    borderRadius: 999,
                    bgcolor: meta.color,
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: 1.3,
                  }}
                >
                  {meta.icon}
                  {meta.label}
                </Box>
              );
            })}
          </Stack>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="https://www.youtube.com/watch?v=..."
            value={urlInput}
            onChange={(e) => {
              setUrlInput(e.target.value);
              if (!urlTouched) setUrlTouched(true);
            }}
            onBlur={() => setUrlTouched(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && urlValid) {
                e.preventDefault();
                handleSubmitAddDialog();
              }
            }}
            error={urlShowError}
            helperText={urlShowError ? 'Please input a valid video url' : ' '}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'var(--color-surface-elevated)',
                borderRadius: 2,
              },
              '& .MuiInputBase-input': {
                color: 'var(--color-text)',
                fontWeight: 500,
              },
            }}
          />

          {/* Preview block — Open Graph metadata fetched server-side
              once the URL passes canPlay. Shows the provider's
              thumbnail, title, and a quick-play strip so the user can
              confirm they've pasted the right link before submitting. */}
          {urlValid && previewSource && (
            <AddVideoPreview
              url={trimmedUrl}
              source={previewSource}
              meta={previewMeta}
              loading={previewLoading}
              error={previewError}
            />
          )}

          {/* Project tagging — mandatory. Multi-select via the same
              MUI Autocomplete pattern used by the Manage Events
              dialog's project filter so the UX stays consistent.
              Submit stays disabled until at least one project is
              picked. */}
          <Typography
            variant="body2"
            sx={{
              color: 'var(--color-text-muted)',
              mt: 2.5,
              mb: 0.5,
              fontWeight: 600,
            }}
          >
            Tag to project(s)
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'var(--color-text-muted)',
              mb: 1,
            }}
          >
            Select up to {MAX_PROJECT_TAGS} projects.
          </Typography>
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            loading={projectOptionsLoading}
            options={projectOptions}
            value={selectedProjectIds
              .map((id) => projectOptions.find((p) => p.projectId === id))
              .filter((p): p is TopViewingSitePlan => Boolean(p))}
            onChange={(_, value) => {
              // Cap silently — `getOptionDisabled` already greys out
              // further options, but a keyboard-driven add (e.g.
              // pasting) can still bypass that. Slice to be safe.
              const next = value
                .slice(0, MAX_PROJECT_TAGS)
                .map((p) => p.projectId);
              setSelectedProjectIds(next);
            }}
            getOptionDisabled={(opt) =>
              selectedProjectIds.length >= MAX_PROJECT_TAGS &&
              !selectedProjectIds.includes(opt.projectId)
            }
            getOptionLabel={(opt) => opt.projectName || opt.projectId}
            isOptionEqualToValue={(option, value) =>
              option.projectId === value.projectId
            }
            renderInput={(params) => (
              <TextField
                {...params}
                autoComplete="off"
                inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                placeholder={
                  selectedProjectIds.length === 0
                    ? 'Select one or more projects'
                    : ''
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'var(--color-surface)',
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((opt, idx) => (
                <Chip
                  {...getTagProps({ index: idx })}
                  key={opt.projectId}
                  label={opt.projectName || opt.projectId}
                  size="small"
                />
              ))
            }
          />
          {projectOptionsError && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                color: 'error.main',
              }}
            >
              {projectOptionsError}
            </Typography>
          )}
          {submitError && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1.5,
                color: 'error.main',
                fontWeight: 600,
              }}
            >
              {submitError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseAddDialog}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: 'var(--color-text-muted)',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitAddDialog}
            disabled={
              !urlValid ||
              submitting ||
              selectedProjectIds.length === 0 ||
              selectedProjectIds.length > MAX_PROJECT_TAGS
            }
            variant="contained"
            startIcon={
              submitting ? (
                <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
              ) : null
            }
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: 'var(--color-accent)',
              color: '#fff',
              '&:hover': {
                bgcolor: 'var(--color-accent-strong, var(--color-accent))',
              },
            }}
          >
            {submitting ? 'Saving…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success confirmation — shown after a submitted video has
          been persisted. Communicates the pending-review state so
          the submitter understands why the new video doesn't appear
          on the public tab yet. */}
      <Dialog
        open={submitSuccessOpen}
        onClose={() => setSubmitSuccessOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ px: 3, pt: 4, pb: 2, textAlign: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <CheckCircleRoundedIcon
              sx={{ fontSize: 64, color: 'success.main' }}
            />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Video submitted
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', lineHeight: 1.5 }}
            >
              Thanks! Your video has been submitted and is awaiting
              admin review. It will appear on the podcast tab once
              approved.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center' }}>
          <Button
            onClick={() => setSubmitSuccessOpen(false)}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 4,
              py: 1,
              borderRadius: 2,
              bgcolor: 'var(--color-accent)',
              color: '#fff',
              '&:hover': {
                bgcolor: 'var(--color-accent-strong, var(--color-accent))',
              },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Back-to-top FAB — fades in once the user has scrolled past
          the initial viewport and smooth-scrolls the tab's scroll
          container back to the top on click. */}
      <Fade in={showBackToTop} unmountOnExit>
        <Fab
          color="primary"
          size="medium"
          aria-label="Back to top"
          onClick={handleScrollToTop}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            zIndex: (theme) => theme.zIndex.tooltip,
            bgcolor: 'var(--color-accent)',
            color: '#fff',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.25)',
            '&:hover': {
              bgcolor: 'var(--color-accent-strong, var(--color-accent))',
            },
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Fade>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// PodcastVideoPlayerDialog
//
// Reusable player dialog driven by a `PodcastVideoRecord`. Exposed so
// the "My Podcasts" and "Manage Podcast Videos" dialogs can open the
// same embedded player a card click triggers on the public tab,
// without duplicating the embed / aspect-ratio plumbing.
//
// We measure the thumbnail's natural aspect when it loads (mirroring
// the card) so portrait reels (Facebook, TikTok, Instagram) and
// landscape clips both render at their true size.
// ---------------------------------------------------------------------------

interface PodcastVideoPlayerDialogProps {
  record: PodcastVideoRecord | null;
  onClose: () => void;
}

export function PodcastVideoPlayerDialog({
  record,
  onClose,
}: PodcastVideoPlayerDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [shareOpen, setShareOpen] = useState(false);

  // Probe the thumbnail's natural aspect ratio on each new record so
  // the dialog dimensions match the actual video. Reset whenever the
  // record changes (or closes) so a stale aspect from a previous
  // video doesn't leak in.
  const [naturalAspect, setNaturalAspect] = useState<number | null>(null);
  useEffect(() => {
    setNaturalAspect(null);
    if (!record?.thumbnailUrl) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setNaturalAspect(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = record.thumbnailUrl;
    return () => {
      cancelled = true;
    };
  }, [record?.id, record?.thumbnailUrl]);

  const video = useMemo<PodcastVideoItem | null>(() => {
    if (!record) return null;
    const base = podcastRecordToVideoItem(record);
    return naturalAspect ? { ...base, naturalAspect } : base;
  }, [record, naturalAspect]);

  const submittedLine =
    record && record.createdByName
      ? `Created by ${record.createdByName}${
          formatCreatedDate(record.createdAt) ? ` on ${formatCreatedDate(record.createdAt)}` : ''
        }`
      : record
        ? `Created by ${record.createdBy}${
            formatCreatedDate(record.createdAt) ? ` on ${formatCreatedDate(record.createdAt)}` : ''
          }`
        : '';

  return (
    <>
    <Dialog
      open={Boolean(record)}
      onClose={onClose}
      maxWidth={
        video
          ? (video.naturalAspect
              ? video.naturalAspect < 1
              : getPlayerAspectRatio(video.source) === '9 / 16')
            ? 'xs'
            : 'md'
          : 'md'
      }
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text)',
          borderRadius: isMobile ? 0 : 3,
          overflow: 'hidden',
          boxShadow: 'var(--color-shadow-card)',
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 10,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.75)' },
          }}
        >
          <CloseIcon />
        </IconButton>

        {video && (
          <Box key={video.id}>
            <PodcastVideoPlayer video={video} />
            <Box sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: 'var(--color-text-strong)',
                  lineHeight: 1.4,
                  mb: 1.5,
                }}
              >
                {video.title}
              </Typography>

              {submittedLine && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <Avatar
                    src={record?.createdByPhoto || undefined}
                    alt={record?.createdByName || record?.createdBy || ''}
                    sx={{ width: 28, height: 28, fontSize: '0.8rem' }}
                  >
                    {(record?.createdByName || record?.createdBy || '?')
                      .charAt(0)
                      .toUpperCase()}
                  </Avatar>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'var(--color-text-muted)',
                      fontWeight: 500,
                    }}
                  >
                    {submittedLine}
                  </Typography>
                </Stack>
              )}

              <Box
                sx={{
                  pt: 1.5,
                  borderTop: '1px solid var(--color-border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'var(--color-text-muted)', fontWeight: 500 }}
                >
                  {getSourceMeta(video.source).label}
                </Typography>

                {(!video.status || video.status === 'verified') && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ShareIcon />}
                    onClick={() => setShareOpen(true)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 1.5,
                      borderColor: 'var(--color-border-strong)',
                      color: 'var(--color-text-strong)',
                      '&:hover': {
                        borderColor: 'var(--color-accent)',
                        bgcolor: 'var(--color-accent-soft)',
                        color: 'var(--color-accent-strong)',
                      },
                    }}
                  >
                    Share
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
    {video && (
      <ShareVideoDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        video={video as PodcastVideoItem}
      />
    )}
    </>
  );
}
